/**
 * Sticky glass header: the user menu, and nothing else.
 *
 * It carries no page title: every screen already prints its own `page-title`
 * directly beneath, and repeating it here said the same thing twice. The
 * navigation collapse control lives in the sidebar, next to what it collapses.
 *
 * The user block reflects the session payload verbatim: the role's display
 * NAME comes from the server, so a role added by an INSERT in a later phase
 * renders correctly with no change here. There is no map from role code to
 * label in this file, and no comparison against one -- the only role-adjacent
 * branch reads `accessState`, which the server computed.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, LogOut, ShieldQuestion } from 'lucide-react';

import { useSignOut } from '@/auth/useSignOut';
import { useSessionScope } from '@/session/SessionScopeContext';

function initialsFor(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function Header() {
  const { accessState, user, role } = useSessionScope();
  const signOut = useSignOut();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const roleName = role?.name ?? 'Unassigned';
  const displayName = user?.display_name ?? 'Not signed in';
  const email = user?.username ?? '';

  // Dismiss the menu on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;

    function onPointerDown(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <header className="atlas-header">
      <div className="header-spacer" />

      <div className="user-menu" ref={menuRef}>
        <button
          type="button"
          className="user-trigger"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="avatar" aria-hidden="true">
            {initialsFor(displayName)}
          </span>
          <span className="user-meta">
            <span className="user-name">{displayName}</span>
            <span className="user-role">{roleName}</span>
          </span>
          <ChevronDown aria-hidden="true" />
        </button>

        {menuOpen ? (
          <div className="user-dropdown" role="menu">
            <div className="dropdown-head">
              <div className="user-name">{displayName}</div>
              <div className="u-email">{email}</div>
            </div>
            {accessState === 'unassigned' ? (
              <div className="dropdown-item" role="menuitem" aria-disabled="true">
                <ShieldQuestion aria-hidden="true" />
                <span>No role granted yet</span>
              </div>
            ) : null}
            <div className="dropdown-sep" />
            <button
              type="button"
              className="dropdown-item"
              role="menuitem"
              onClick={() => void signOut()}
            >
              <LogOut aria-hidden="true" />
              <span>Sign out</span>
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
